import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FixedDataPicker } from '../fixed-data-picker/FixedDataPicker';
import { IPickerProps } from '../types';

import { EnumEntityLike, EnumValue } from './EnumEntityLikePicker';

export interface EnumPickerProps
  extends Omit<
    IPickerProps<EnumEntityLike, false>,
    'multiple' | 'value' | 'onChange'
  > {
  value: EnumValue;
  onChange: (input: EnumValue) => void;
  values: EnumValue[];
  translationPrefix?: string;
}

export function EnumPicker(props: EnumPickerProps) {
  const { values, value, onChange, translationPrefix, ...rest } = props;

  const { t } = useTranslation();

  const dataSet = useMemo<EnumEntityLike[]>(() => {
    const prefix = translationPrefix || 'ENUMS';
    return values
      .map((x) => {
        const name = x.toString().toUpperCase();
        return { id: x, name: t(`${prefix}.${name}`) };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [translationPrefix, values, t]);

  const wrappedValue = useMemo(() => {
    return dataSet.find((x) => x.id === value);
  }, [dataSet, value]);

  const handleChange = useCallback(
    (newValue: EnumEntityLike): void => {
      onChange(newValue.id);
    },
    [onChange]
  );

  return (
    <FixedDataPicker
      {...rest}
      multiple={false}
      dataSet={dataSet}
      value={wrappedValue}
      onChange={handleChange}
    />
  );
}
